import {ComponentType, Ref, useCallback, useEffect, useRef, useState} from 'react';
import {AnimationState} from '../transition';
import {getName, setRef, tryCall} from '../utils';
import {ModalManager} from './modal-manager';

export type ModalManagerRef = Ref<ModalManager>;

export type ManagerWrapperProps = { initiallyHidden?: boolean; modalManagerRef?: ModalManagerRef; unmountOnHide?: boolean; };

export type ModalManagerProps = { open: boolean; onTransitionStateChanged: (state: AnimationState) => void; };

type TransitionInfo = { callback?: Function, open: boolean };


/**
 * @param Component - target React component. Component props should contain ModalManagerProps.
 * */
export function withModalManager<T extends ModalManagerProps>(Component: ComponentType<T>): ComponentType<Omit<T, 'onTransitionStateChanged' | 'open'> & ManagerWrapperProps> {
	function ModalManagerWrapper(props: T & ManagerWrapperProps): JSX.Element {
		const [open, setOpen] = useState(!props.initiallyHidden);
		const [transitionInfo, setTransitionInfo] = useState<TransitionInfo>(null);

		const toggleModal = useCallback((toggle: boolean, callback: Function) => setOpen(open => {
			if (open === toggle) {
				tryCall(callback);
			} else {
				setTransitionInfo({callback, open: toggle});
			}
			return toggle;
		}), []);

		const manager = useRef(new ModalManager(
			(callback) => toggleModal(true, callback),
			(callback) => toggleModal(false, callback),
		));

		useEffect(() => setRef(manager.current, props.modalManagerRef), [props.modalManagerRef]);
		useEffect(() => () => manager.current?.destroy(), []);

		const handleTransitionEnd = useCallback((state: AnimationState) => {
			if ((state === 'enter' && transitionInfo?.open) || (state === 'exit' && !(transitionInfo?.open))) {
				tryCall(transitionInfo?.callback);
				setTransitionInfo(undefined);
			}
		}, [transitionInfo]);

		if (!props.unmountOnHide && !open && !transitionInfo) {
			return;
		}

		return <Component open={open} onTransitionStateChanged={handleTransitionEnd} {...props as T}/>;
	}

	ModalManagerWrapper.displayName = getName(Component);
	return ModalManagerWrapper as any;
}
